import React from 'react';
import { ModelSettings, ModelType } from './types';
import { useAdminTranslation } from '../../../modules/i18n/hooks/useTranslation';

interface ModelCardProps {
  modelType: ModelType;
  settings: ModelSettings;
  role: 'primary' | 'fallback';
  onConfigure: () => void;
}

const getProviderColors = (provider: string) => {
  switch (provider.toLowerCase()) {
    case 'openai':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    case 'baai':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    case 'cohere':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
    case 'jina':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
    case 'anthropic':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
    case 'vllm':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900/20 dark:text-teal-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-dark-background/20 dark:text-dark-text-secondary';
  }
};

const ModelCard: React.FC<ModelCardProps> = ({ modelType, settings, role, onConfigure }) => {
  const { t } = useAdminTranslation();
  const providerColors = getProviderColors(settings.provider);
  const isPrimary = role === 'primary';

  return (
    <div className={`
      relative overflow-hidden rounded-xl border bg-white shadow-sm transition-all hover:shadow-lg dark:bg-dark-background-secondary
      ${isPrimary
        ? 'border-blue-200 dark:border-blue-800 hover:border-blue-300 dark:hover:border-blue-700'
        : 'border-orange-200 dark:border-orange-800 hover:border-orange-300 dark:hover:border-orange-700'
      }
    `}>
      {/* Content */}
      <div className="p-4 sm:p-6">
        {/* Header with Role Badge */}
        <div className="mb-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          {/* Left: Active status + Model name */}
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            {/* Active status indicator */}
            <div className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${
              settings.isActive ? 'bg-green-500 shadow-sm shadow-green-500/50' : 'bg-gray-300 dark:bg-dark-background-tertiary'
            }`} />
            <h2 className="text-base font-bold text-gray-900 dark:text-dark-text truncate" title={settings.modelName}>
              {settings.modelName}
            </h2>
          </div>

          {/* Right: Role Badge */}
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold flex-shrink-0 ${
            isPrimary
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
              : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
          }`}>
            {isPrimary ? t('models.primaryModel') : t('models.fallbackModel')}
          </span>
        </div>

        {/* Provider badge and auto-configured text */}
        <div className="flex items-center gap-2 pl-4 flex-wrap">
          <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${providerColors}`}>
            {settings.provider}
          </span>
          {!isPrimary && (
            <span className="text-xs text-gray-500 dark:text-dark-text-secondary">
              {t('models.autoConfigured')}
            </span>
          )}
        </div>
      </div>

      {/* Parameters */}
      <div className="space-y-2">
        {modelType === 'llm' && (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 p-3 dark:from-gray-900/50 dark:to-gray-900/30 border border-gray-200 dark:border-border-dark">
              <div className="text-xs font-medium text-gray-600 dark:text-dark-text-secondary mb-1">Temperature</div>
              <div className="text-base font-bold text-gray-900 dark:text-dark-text">
                {settings.temperature ?? 'N/A'}
              </div>
            </div>
            <div className="rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 p-3 dark:from-gray-900/50 dark:to-gray-900/30 border border-gray-200 dark:border-border-dark">
              <div className="text-xs font-medium text-gray-600 dark:text-dark-text-secondary mb-1">Max Tokens</div>
              <div className="text-base font-bold text-gray-900 dark:text-dark-text">
                {settings.maxTokens?.toLocaleString() ?? 'N/A'}
              </div>
            </div>
          </div>
        )}
        {modelType === 'embedding' && (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 p-3 dark:from-gray-900/50 dark:to-gray-900/30 border border-gray-200 dark:border-border-dark">
              <div className="text-xs font-medium text-gray-600 dark:text-dark-text-secondary mb-1">Dimensions</div>
              <div className="text-base font-bold text-gray-900 dark:text-dark-text">
                {settings.dimensions?.toLocaleString() ?? 'N/A'}
              </div>
            </div>
            <div className="rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 p-3 dark:from-gray-900/50 dark:to-gray-900/30 border border-gray-200 dark:border-border-dark">
              <div className="text-xs font-medium text-gray-600 dark:text-dark-text-secondary mb-1">Batch Size</div>
              <div className="text-base font-bold text-gray-900 dark:text-dark-text">
                {settings.batchSize ?? 'N/A'}
              </div>
            </div>
          </div>
        )}
        {modelType === 'ranking' && (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 p-3 dark:from-gray-900/50 dark:to-gray-900/30 border border-gray-200 dark:border-border-dark">
              <div className="text-xs font-medium text-gray-600 dark:text-dark-text-secondary mb-1">Top K</div>
              <div className="text-base font-bold text-gray-900 dark:text-dark-text">
                {settings.topK ?? 'N/A'}
              </div>
            </div>
            <div className="rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 p-3 dark:from-gray-900/50 dark:to-gray-900/30 border border-gray-200 dark:border-border-dark">
              <div className="text-xs font-medium text-gray-600 dark:text-dark-text-secondary mb-1">Threshold</div>
              <div className="text-base font-bold text-gray-900 dark:text-dark-text">
                {settings.threshold ?? 'N/A'}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Footer - Settings Button */}
      <div className="border-t border-gray-200 dark:border-border-dark bg-primary dark:bg-primary">
        <button
          onClick={onConfigure}
          className="w-full p-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 dark:hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
        >
          <div className="flex items-center justify-center gap-2">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {t('models.settings')}
          </div>
        </button>
      </div>
    </div>
  );
};

export default ModelCard;